import { gql } from "@apollo/client";
import {
  CustomerAddressDeepNestingFragment,
  CustomerDeepNestingFragment,
  CustomerListDeepNestingFragment,
  PaginatedListMetaDeepNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  ContactPreference,
  Gender,
} from "@towersystems/roam-common/lib/generated-types";
import * as Yup from "yup";
import { oneOfEnum } from "../../../../utilities/transforms";

export const QUERY_CUSTOMERS_LIST = gql`
  ${CustomerDeepNestingFragment}
  ${PaginatedListMetaDeepNestingFragment}
  ${CustomerAddressDeepNestingFragment}
  ${CustomerListDeepNestingFragment}
  query customers($options: CustomerListOptions) {
    customers(options: $options) {
      ...CustomerListDeepNesting
    }
  }
`;

export const QUERY_CUSTOMER = gql`
  ${CustomerDeepNestingFragment}
  ${CustomerAddressDeepNestingFragment}
  query customer($id: ID!) {
    customer(id: $id) {
      ...CustomerDeepNesting
    }
  }
`;

export const MUTATION_CREATE_CUSTOMER = gql`
  ${CustomerAddressDeepNestingFragment}
  ${CustomerDeepNestingFragment}
  mutation createCustomer($input: CustomerInput!) {
    createCustomer(input: $input) {
      ...CustomerDeepNesting
    }
  }
`;

export const MUTATION_UPDATE_CUSTOMER = gql`
  ${CustomerAddressDeepNestingFragment}
  ${CustomerDeepNestingFragment}
  mutation updateCustomer($input: CustomerInput!) {
    updateCustomer(input: $input) {
      ...CustomerDeepNesting
    }
  }
`;

export const addressInputSchema = Yup.object({
  id: Yup.string().optional().default(undefined),
  address1: Yup.string().required(),
  address2: Yup.string().optional().nullable(),
  city: Yup.string().required(),
  postcode: Yup.string().required(),
  state: Yup.string().required(),
  country: Yup.string().required(),
}).required();

const customerAddressSchema = addressInputSchema.shape({
  isPrimary: Yup.boolean().optional(),
});

// Loose international phone validation: allow +, spaces, dashes, parentheses and
// dots, and require 6-15 digits total. Supports NZ/AU/international numbers while
// still rejecting letters and obviously-too-short input. Field remains optional.
const PHONE_ALLOWED_CHARS = /^[+\d\s().-]+$/;

const mobileNumberSchema = Yup.string()
  .test("valid-mobile-number", "Invalid mobile number", (value) => {
    if (!value) return true; // optional - empty/undefined is valid
    if (!PHONE_ALLOWED_CHARS.test(value)) return false;
    const digitCount = value.replace(/\D/g, "").length;
    return digitCount >= 6 && digitCount <= 15;
  })
  .optional();

export const customerInputSchema = Yup.object({
  id: Yup.string().optional(),
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  emailAddress: Yup.string().email().optional(),
  phoneNumber: Yup.string().optional(),
  mobileNumber: mobileNumberSchema,
  gender: oneOfEnum(Gender).optional(),
  contactPreference: oneOfEnum(ContactPreference).optional(),
  addressesToAdd: Yup.array(
    customerAddressSchema.required().noUnknown()
  ).optional(),
  addressesToRemove: Yup.array(Yup.string().required()).optional(),
}).required();

export const createCustomerInputSchema = Yup.object({
  id: Yup.string().optional(),
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  emailAddress: Yup.string().email().required(),
  phoneNumber: Yup.string().optional(),
  mobileNumber: mobileNumberSchema,
  gender: oneOfEnum(Gender).optional(),
  contactPreference: oneOfEnum(ContactPreference).optional(),
  addressesToAdd: Yup.array(
    customerAddressSchema.required().noUnknown()
  ).optional(),
  addressesToRemove: Yup.array(Yup.string().required()).optional(),
}).required();

export const defaultCustomerInputValues = {
  id: undefined,
  firstName: "",
  lastName: "",
  emailAddress: undefined,
  phoneNumber: "",
  mobileNumber: "",
  gender: Gender.UNKNOWN,
  contactPreference: ContactPreference.UNSET,
  addressesToAdd: [],
  addressesToRemove: [],
};

export const CustomerFormSections = {
  DETAILS: "Customer Information",
  SETTINGS: "Settings",
} as const;
